import React, { useEffect, useRef } from 'react';

// NeuronBackground — full-deck neural network animation.
//
// Narrative intent (set by user):
//   "Brain animation of neuron which should be animated through out the pitch
//    deck. Should merge with the background screen, pulsating nerves should
//    run all over the deck — neural information passing — without disturbing
//    the text aligned within."
//
// Visual system:
//   • A dense graph of ~140 neurons spans the entire viewport.
//   • Each neuron has 3–4 axonal connections to its nearest neighbours.
//   • Background "data streams" — small comet-like sprites — travel along
//     edges constantly so information feels like it's flowing all over the
//     deck at all times.
//   • A wandering "bullet pressure" hotspot drifts in a slow Lissajous path.
//     Inside its radius, the cool cyan baseline shifts to warm amber/red
//     and neurons fire much faster — the bullet irritating one region at a
//     time. The hotspot's center, radius, and speed all wobble subtly.
//   • A horizontal "soft text band" mask gently dims the central left
//     column (where most segment copy lives) so the animation never visually
//     competes with reading — but the rest of the deck stays vivid.
//   • Whole layer composites via mix-blend-mode: screen at a moderate base
//     opacity so it MERGES with the deck rather than overlaying it.
//
// All painted to a single 2D canvas (no React state, no library), ~70 long-
// lived pulses + ~60 short comet streams on screen at any time. Animates at
// 60fps on a mid-range laptop.
export default function NeuronBackground({ opacity = 0.28 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let raf;
    let mounted = true;

    // ---------- Sizing ----------
    let width = 0;
    let height = 0;
    const setSize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = width + 'px';
      canvas.style.height = height + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    setSize();
    window.addEventListener('resize', setSize);

    // ---------- Build the neural graph ----------
    // Density scales with viewport area so the pulse pattern looks the same
    // on phones and 4k monitors.
    const density = Math.min(180, Math.max(90, Math.round((width * height) / 16000)));
    const neurons = [];
    for (let i = 0; i < density; i++) {
      // Push a few neurons toward the edges so the pulses cross the entire
      // viewport, not just cluster centrally.
      const edgeBias = Math.random() < 0.25 ? 1 : 0;
      const x = edgeBias
        ? (Math.random() < 0.5 ? Math.random() * width * 0.18 : width - Math.random() * width * 0.18)
        : Math.random() * width;
      const y = edgeBias
        ? (Math.random() < 0.5 ? Math.random() * height * 0.18 : height - Math.random() * height * 0.18)
        : Math.random() * height;
      neurons.push({
        x,
        y,
        baseR: 0.7 + Math.random() * 1.7,
        phase: Math.random() * Math.PI * 2,
        drift: 0.18 + Math.random() * 0.35,
        fire: Math.random() * 0.4,
      });
    }

    // 3-nearest-neighbour adjacency (one-time O(n²)).
    const adjacency = neurons.map((n, i) => {
      const dists = neurons
        .map((m, j) => ({ j, d: (m.x - n.x) ** 2 + (m.y - n.y) ** 2 }))
        .filter((e) => e.j !== i)
        .sort((a, b) => a.d - b.d)
        .slice(0, 4); // up to 4 connections per neuron for richer paths
      return dists.map((e) => e.j);
    });

    // ---------- Pulses (information packets travelling along axons) ----------
    const pulses = [];
    const spawnPulse = (from, intensity = 1) => {
      const neighbours = adjacency[from];
      if (!neighbours.length) return;
      const to = neighbours[Math.floor(Math.random() * neighbours.length)];
      pulses.push({
        from,
        to,
        t: 0,
        speed: 0.010 + Math.random() * 0.020,
        intensity,
        // Long-tail comet length, more variety
        trail: 8 + Math.random() * 24,
      });
    };

    // ---------- Independent "data stream" comets ----------
    // Even when no neuron is firing nearby, a steady ambient stream of small
    // sparks travels across the canvas so the deck always feels "alive".
    const streams = [];
    const spawnStream = () => {
      const startSide = Math.floor(Math.random() * 4);
      let x, y, vx, vy;
      const speed = 0.5 + Math.random() * 1.3;
      if (startSide === 0) { x = -10; y = Math.random() * height; vx = speed; vy = (Math.random() - 0.5) * 0.4; }
      else if (startSide === 1) { x = width + 10; y = Math.random() * height; vx = -speed; vy = (Math.random() - 0.5) * 0.4; }
      else if (startSide === 2) { x = Math.random() * width; y = -10; vx = (Math.random() - 0.5) * 0.4; vy = speed; }
      else { x = Math.random() * width; y = height + 10; vx = (Math.random() - 0.5) * 0.4; vy = -speed; }
      streams.push({ x, y, vx, vy, life: 0, maxLife: 220 + Math.random() * 280, w: 0.6 + Math.random() * 1.4 });
    };

    // ---------- The 'bullet pressure' hotspot ----------
    const hotspot = {
      x: width * 0.5,
      y: height * 0.45,
      r: Math.min(width, height) * 0.28,
    };

    // ---------- Render loop ----------
    let lastSpawn = 0;
    let lastStream = 0;
    const start = performance.now();
    const render = (now) => {
      if (!mounted) return;
      const t = (now - start) / 1000;

      // Slow Lissajous-style drift for the bullet pressure region
      hotspot.x = width * 0.5 + Math.sin(t * 0.13) * width * 0.32 + Math.sin(t * 0.07) * width * 0.10;
      hotspot.y = height * 0.5 + Math.cos(t * 0.11) * height * 0.28 + Math.sin(t * 0.05) * height * 0.08;
      // Subtle pulse on the radius itself
      hotspot.r = Math.min(width, height) * (0.24 + 0.05 * Math.sin(t * 0.6));

      ctx.clearRect(0, 0, width, height);

      // -------- 1) Synapse edges (faint web of connections) --------
      ctx.lineCap = 'round';
      for (let i = 0; i < neurons.length; i++) {
        const a = neurons[i];
        const neigh = adjacency[i];
        for (let k = 0; k < neigh.length; k++) {
          const b = neurons[neigh[k]];
          if (!b) continue;
          if (neigh[k] < i) continue; // draw each edge once

          const mx = (a.x + b.x) * 0.5;
          const my = (a.y + b.y) * 0.5;
          const dx = mx - hotspot.x;
          const dy = my - hotspot.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const heat = Math.max(0, 1 - dist / hotspot.r);

          // Cool cyan → warm amber/red as heat climbs
          const r = Math.round(110 + heat * 145);
          const g = Math.round(220 - heat * 130);
          const b2 = Math.round(255 - heat * 200);

          // Slow opacity pulse along each edge so the whole web breathes
          const breathe = 0.55 + 0.45 * Math.sin(t * 0.4 + (a.x + b.y) * 0.003);
          ctx.strokeStyle = `rgba(${r},${g},${b2},${(0.06 + heat * 0.25) * breathe})`;
          ctx.lineWidth = 0.5 + heat * 0.4;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }

      // -------- 2) Neurons (nodes + halos) --------
      for (let i = 0; i < neurons.length; i++) {
        const n = neurons[i];
        const twinkle = 0.5 + 0.5 * Math.sin(t * n.drift + n.phase);
        const dx = n.x - hotspot.x;
        const dy = n.y - hotspot.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const heat = Math.max(0, 1 - dist / hotspot.r);

        n.fire = Math.max(0, n.fire - 0.012);
        if (heat > 0.3 && Math.random() < heat * 0.05) {
          n.fire = Math.min(1, n.fire + 0.6);
          if (pulses.length < 240) spawnPulse(i, 0.8 + heat * 0.4);
        }

        const r = n.baseR * (1 + twinkle * 0.45 + n.fire * 1.5 + heat * 0.6);
        const mix = Math.min(1, heat + n.fire * 0.6);
        const cR = Math.round(120 + mix * 135);
        const cG = Math.round(230 - mix * 140);
        const cB = Math.round(255 - mix * 210);
        const alpha = 0.36 + twinkle * 0.20 + n.fire * 0.6 + heat * 0.28;

        // Outer halo
        ctx.fillStyle = `rgba(${cR},${cG},${cB},${alpha * 0.18})`;
        ctx.beginPath();
        ctx.arc(n.x, n.y, r * 4.5, 0, Math.PI * 2);
        ctx.fill();
        // Core
        ctx.fillStyle = `rgba(${cR},${cG},${cB},${Math.min(1, alpha)})`;
        ctx.beginPath();
        ctx.arc(n.x, n.y, r, 0, Math.PI * 2);
        ctx.fill();
      }

      // -------- 3) Pulses (information packets along axons) --------
      for (let p = pulses.length - 1; p >= 0; p--) {
        const pu = pulses[p];
        pu.t += pu.speed;
        if (pu.t >= 1) {
          const target = neurons[pu.to];
          if (target) {
            target.fire = Math.min(1, target.fire + 0.55 * pu.intensity);
            // 65% chance pulse continues forward — propagating information
            if (Math.random() < 0.65) spawnPulse(pu.to, pu.intensity * 0.88);
          }
          pulses.splice(p, 1);
          continue;
        }
        const a = neurons[pu.from];
        const b = neurons[pu.to];
        if (!a || !b) { pulses.splice(p, 1); continue; }
        const px = a.x + (b.x - a.x) * pu.t;
        const py = a.y + (b.y - a.y) * pu.t;
        const dx = px - hotspot.x;
        const dy = py - hotspot.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const heat = Math.max(0, 1 - dist / hotspot.r);
        const cR = Math.round(190 + heat * 65);
        const cG = Math.round(245 - heat * 170);
        const cB = Math.round(255 - heat * 210);

        // Trailing comet — short line from a-step-back to the current point
        const tBack = Math.max(0, pu.t - pu.trail * 0.005);
        const tx = a.x + (b.x - a.x) * tBack;
        const ty = a.y + (b.y - a.y) * tBack;
        const grad = ctx.createLinearGradient(tx, ty, px, py);
        grad.addColorStop(0, `rgba(${cR},${cG},${cB},0)`);
        grad.addColorStop(1, `rgba(${cR},${cG},${cB},${0.75 * pu.intensity})`);
        ctx.strokeStyle = grad;
        ctx.lineWidth = 1.0;
        ctx.beginPath();
        ctx.moveTo(tx, ty);
        ctx.lineTo(px, py);
        ctx.stroke();

        // Bright head + halo
        ctx.fillStyle = `rgba(${cR},${cG},${cB},${0.85 * pu.intensity})`;
        ctx.beginPath();
        ctx.arc(px, py, 1.7, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = `rgba(${cR},${cG},${cB},${0.18 * pu.intensity})`;
        ctx.beginPath();
        ctx.arc(px, py, 5.5, 0, Math.PI * 2);
        ctx.fill();
      }

      // -------- 4) Ambient streams (decorative data comets) --------
      for (let s = streams.length - 1; s >= 0; s--) {
        const sm = streams[s];
        sm.life += 1;
        sm.x += sm.vx;
        sm.y += sm.vy;
        if (sm.life > sm.maxLife || sm.x < -20 || sm.x > width + 20 || sm.y < -20 || sm.y > height + 20) {
          streams.splice(s, 1);
          continue;
        }
        // Tint by hotspot heat at current location
        const dx = sm.x - hotspot.x;
        const dy = sm.y - hotspot.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const heat = Math.max(0, 1 - dist / hotspot.r);
        const cR = Math.round(150 + heat * 105);
        const cG = Math.round(235 - heat * 160);
        const cB = Math.round(255 - heat * 215);
        // Comet head
        ctx.fillStyle = `rgba(${cR},${cG},${cB},0.55)`;
        ctx.beginPath();
        ctx.arc(sm.x, sm.y, sm.w, 0, Math.PI * 2);
        ctx.fill();
        // Trail (10px back)
        const tx = sm.x - sm.vx * 8;
        const ty = sm.y - sm.vy * 8;
        const grad = ctx.createLinearGradient(tx, ty, sm.x, sm.y);
        grad.addColorStop(0, `rgba(${cR},${cG},${cB},0)`);
        grad.addColorStop(1, `rgba(${cR},${cG},${cB},0.55)`);
        ctx.strokeStyle = grad;
        ctx.lineWidth = sm.w;
        ctx.beginPath();
        ctx.moveTo(tx, ty);
        ctx.lineTo(sm.x, sm.y);
        ctx.stroke();
      }

      // -------- Spawn cadence --------
      // Ambient pulse seeding (so even outside the hotspot, the brain is
      // never silent — neural information is always passing).
      if (now - lastSpawn > 120) {
        lastSpawn = now;
        // Spawn 1–2 ambient pulses anywhere on the graph
        for (let k = 0; k < 2; k++) {
          const idx = Math.floor(Math.random() * neurons.length);
          spawnPulse(idx, 0.65 + Math.random() * 0.25);
        }
      }
      if (now - lastStream > 260 && streams.length < 40) {
        lastStream = now;
        spawnStream();
      }

      raf = requestAnimationFrame(render);
    };
    raf = requestAnimationFrame(render);

    return () => {
      mounted = false;
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', setSize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="neuron-background"
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1,
        opacity,
        // Screen blend so the cyan glints ADD against the dark deck rather
        // than overlaying it — guaranteeing it never darkens text legibility.
        mixBlendMode: 'screen',
        // A radial mask softens the centre-left column (the text rail) so the
        // pulses stay subliminal where most copy sits, but remain rich in the
        // outer / right band where the hologram visuals live.
        WebkitMaskImage:
          'radial-gradient(ellipse 95% 110% at 70% 50%, rgba(0,0,0,1) 0%, rgba(0,0,0,0.78) 45%, rgba(0,0,0,0.45) 78%, rgba(0,0,0,0.6) 100%)',
        maskImage:
          'radial-gradient(ellipse 95% 110% at 70% 50%, rgba(0,0,0,1) 0%, rgba(0,0,0,0.78) 45%, rgba(0,0,0,0.45) 78%, rgba(0,0,0,0.6) 100%)',
      }}
    />
  );
}
